import type {
  OnboardingTrainingPackageValue,
  OnboardingTrainingTopic,
} from './OnboardingWizard.types';
import {
  PACKAGE_MAX_TRAINING_TOPICS,
  RECOMMENDATION_MAX_TRAINING_TOPICS,
  getMaxTrainingTopicsForPackage,
} from '../../constants/recommendationProfileOptions';

/** Industry options — manager-approved list, aligned with the training matrix. */
export const ONBOARDING_TRAINING_PACKAGE_OPTIONS = [
  { value: 'essentials', labelKey: 'essentials' },
  { value: 'pro', labelKey: 'pro' },
  { value: 'plus', labelKey: 'plus' },
] as const;

export const ONBOARDING_INDUSTRY_OPTIONS = [
  { value: 'Financial Services', label: 'Financial Services' },
  { value: 'Healthcare and Life Sciences', label: 'Healthcare and Life Sciences' },
  { value: 'Technology and Telecommunications', label: 'Technology and Telecommunications' },
  { value: 'Manufacturing and Industrial', label: 'Manufacturing and Industrial' },
  { value: 'Energy, Utilities, and Natural Resources', label: 'Energy, Utilities, and Natural Resources' },
  { value: 'Retail, Consumer Goods, and Hospitality', label: 'Retail, Consumer Goods, and Hospitality' },
  { value: 'Professional and Business Services', label: 'Professional and Business Services' },
  { value: 'Transportation, Logistics, and Infrastructure', label: 'Transportation, Logistics, and Infrastructure' },
  { value: 'Public Sector, Education, and Nonprofit', label: 'Public Sector, Education, and Nonprofit' },
  { value: 'Media, Entertainment, and Real Estate', label: 'Media, Entertainment, and Real Estate' },
] as const;

/** Employee count bands — recorded for reporting; not used by the recommendation engine. */
export const ONBOARDING_EMPLOYEE_COUNT_OPTIONS = [
  { value: '1-500', label: '1 – 500' },
  { value: '1,000-5,000', label: '1,000 – 5,000' },
  { value: '5,000-10,000', label: '5,000 – 10,000' },
  { value: '10,000-30,000', label: '10,000 – 30,000' },
  { value: '30,000-50,000', label: '30,000 – 50,000' },
  { value: '50,000+', label: '50,000+' },
] as const;

/** Two-column geographic region layout (manager-approved list). */
export const ONBOARDING_REGION_COLUMNS: readonly [readonly string[], readonly string[]] = [
  [
    'North America',
    'Central America and the Caribbean',
    'South America',
    'Europe',
  ],
  [
    'Middle East',
    'Africa',
    'Asia',
    'Australia, New Zealand, and Pacific Islands',
  ],
] as const;

export const ONBOARDING_REGIONS = [
  ...ONBOARDING_REGION_COLUMNS[0],
  ...ONBOARDING_REGION_COLUMNS[1],
] as const;

export type OnboardingRegion = (typeof ONBOARDING_REGIONS)[number];

export const ONBOARDING_PACKAGE_MAX_TRAINING_TOPICS = PACKAGE_MAX_TRAINING_TOPICS;

/** Upper bound for Plus — use {@link getOnboardingMaxTrainingTopicsForPackage} per package. */
export const ONBOARDING_MAX_TRAINING_TOPICS = RECOMMENDATION_MAX_TRAINING_TOPICS;

export const getOnboardingMaxTrainingTopicsForPackage =
  getMaxTrainingTopicsForPackage as (
    trainingPackage: OnboardingTrainingPackageValue | '' | string,
  ) => number;

export function trimTrainingTopicsToPackageLimit(
  trainingTopics: OnboardingTrainingTopic[],
  trainingPackage: OnboardingTrainingPackageValue | '' | string,
): OnboardingTrainingTopic[] {
  const max = getOnboardingMaxTrainingTopicsForPackage(trainingPackage);
  return trainingTopics.slice(0, max);
}

/** Two-column training topics — A–Z, read left-to-right across each row. */
export const ONBOARDING_TRAINING_TOPIC_COLUMNS: readonly [
  readonly { id: string; name: string }[],
  readonly { id: string; name: string }[],
] = [
  [
    { id: 'ai-responsible-use', name: 'AI Responsible Use' },
    { id: 'anti-money-laundering', name: 'Anti-Money Laundering' },
    { id: 'conflicts-of-interest', name: 'Conflicts of Interest' },
    { id: 'fair-competition', name: 'Fair Competition' },
    { id: 'gdpr', name: 'GDPR' },
    { id: 'hipaa', name: 'HIPAA' },
    { id: 'insider-trading', name: 'Insider Trading' },
    { id: 'manager-responsibility', name: 'Manager Responsibility' },
    { id: 'privacy', name: 'Privacy' },
    { id: 'speak-up-and-non-retaliation', name: 'Speak Up and Non-Retaliation' },
    { id: 'trade-compliance', name: 'Trade Compliance' },
    { id: 'welcome-to-the-code-of-conduct-course', name: 'Welcome to the Code of Conduct Course' },
  ],
  [
    { id: 'anti-harassment-and-bullying', name: 'Anti-Harassment and Bullying' },
    { id: 'bribery-and-corruption', name: 'Bribery and Corruption' },
    { id: 'cybersecurity', name: 'Cybersecurity' },
    { id: 'fraud', name: 'Fraud' },
    { id: 'gifts-and-entertainment', name: 'Gifts and Entertainment' },
    { id: 'import-export-controls', name: 'Import Export Controls' },
    {
      id: 'interaction-with-healthcare-professionals',
      name: 'Interaction with Healthcare Professionals',
    },
    { id: 'modern-slavery-prevention', name: 'Modern Slavery Prevention' },
    { id: 'respect-in-the-workplace', name: 'Respect in the Workplace' },
    { id: 'third-party-expectations', name: 'Third Party Expectations' },
    { id: 'trade-sanctions', name: 'Trade Sanctions' },
  ],
] as const;

export const ONBOARDING_MAX_MONTH_SELECTIONS = 6;

export const ONBOARDING_MAX_ADDITIONAL_CONTEXT_LENGTH = 300;

export const ONBOARDING_MONTH_OPTIONS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
].map((month) => ({ value: month, label: month }));
